import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  Paper,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useData } from '../../context/DataContext';
import RichTextEditor from '../common/RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import DraggableList from '../common/DraggableList';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as DepartmentIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

const ManageTeamMembers = () => {
  const { state, dispatch } = useData();
  const { teamMembers } = state;

  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    image: '',
    bio: '',
    department: '',
    imageFile: null,
    imagePreview: null
  });

  const departments = [
    'Executive Leadership',
    'Medical Staff',
    'Nursing',
    'Administration',
    'Support Staff',
    'IT Department',
    'Finance',
    'Human Resources'
  ];

  const handleOpenDialog = (member = null, department = '') => {
    if (member) {
      setEditingMember(member);
      setSelectedDepartment(department);
      setFormData({
        name: member.name || '',
        position: member.position || '',
        image: member.image || '',
        bio: member.bio || '',
        department: department,
        imageFile: null,
        imagePreview: null
      });
    } else {
      setEditingMember(null);
      setSelectedDepartment('');
      setFormData({
        name: '',
        position: '',
        image: '',
        bio: '',
        department: '',
        imageFile: null,
        imagePreview: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    // Cleanup image preview URL to prevent memory leaks
    if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    
    setOpenDialog(false);
    setEditingMember(null);
    setFormData({
      name: '',
      position: '',
      image: '',
      bio: '',
      department: '',
      imageFile: null,
      imagePreview: null
    });
  };

  // Cleanup function for image preview URLs
  useEffect(() => {
    return () => {
      if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.imagePreview);
      }
    };
  }, [formData.imagePreview]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, or GIF)');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setFormData({
        ...formData,
        imageFile: file,
        imagePreview: previewUrl
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const memberData = {
        name: formData.name,
        position: formData.position,
        bio: formData.bio,
        department: selectedDepartment
      };

      if (editingMember) {
        // Update existing member
        const result = await dispatch({
          type: 'UPDATE_TEAM_MEMBER',
          payload: {
            ...memberData,
            id: editingMember.id || editingMember.name + editingMember.position
          },
          imageFile: formData.imageFile
        });
        
        if (result && result.success) {
          handleCloseDialog();
        } else {
          alert('Failed to update team member. Please try again.');
        }
      } else {
        // Add new member
        const result = await dispatch({
          type: 'ADD_TEAM_MEMBER',
          payload: memberData,
          imageFile: formData.imageFile
        });
        
        if (result && result.success) {
          handleCloseDialog();
        } else {
          alert('Failed to add team member. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('An error occurred while saving the team member. Please try again.');
    }
  };

  const handleDeleteMember = async (memberToDelete, departmentName) => {
    try {
      // Call the enhanced dispatch to delete from database
      const result = await dispatch({
        type: 'DELETE_TEAM_MEMBER',
        payload: memberToDelete.id
      });
      
      if (result && result.success) {
        // Success - the enhanced dispatch will update the state
        console.log('Team member deleted successfully');
      } else {
        alert('Failed to delete team member. Please try again.');
      }
    } catch (deleteError) {
      console.error('Error deleting team member:', deleteError);
      alert('An error occurred while deleting the team member. Please try again.');
    }
  };

  const handleReorder = async (reorderedMembers, department = null) => {
    try {
      // Update the full teamMembers array with new order
      const updatedTeamMembers = teamMembers.map(member => {
        const reorderedMember = reorderedMembers.find(rm => rm.id === member.id);
        if (reorderedMember) {
          return reorderedMember;
        }
        return member;
      });
      
      // Update local state immediately for instant UI feedback
      dispatch({
        type: 'REORDER_TEAM_MEMBERS',
        payload: updatedTeamMembers
      });
      
      // Then update the database in the background
      const updatePromises = reorderedMembers.map((member, index) => 
        dispatch({
          type: 'UPDATE_TEAM_MEMBER',
          payload: {
            ...member,
            orderIndex: index
          }
        })
      );
      
      await Promise.all(updatePromises);
      console.log('Team members reordered successfully');
    } catch (error) {
      console.error('Error reordering team members:', error);
      alert('Failed to save new order. Please try again.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Manage Team Members
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Team Member
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Add, edit, or remove team members organized by departments. Drag to reorder within each department - changes will be reflected immediately on the public website.
      </Typography>

      {/* Team Members by Department */}
      {(!teamMembers || teamMembers.length === 0) ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No team members added yet. Click "Add Team Member" to get started.
          </Typography>
        </Paper>
      ) : (
        // Group team members by department for display
        (() => {
          const groupedMembers = teamMembers.reduce((acc, member) => {
            const dept = member.department || 'General';
            if (!acc[dept]) {
              acc[dept] = [];
            }
            acc[dept].push(member);
            return acc;
          }, {});

          return Object.entries(groupedMembers).map(([departmentName, members]) => (
            <Paper key={departmentName} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                  {departmentName} ({members.length} members)
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    Drag to reorder
                  </Typography>
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog(null, departmentName)}
                  size="small"
                >
                  Add Member
                </Button>
              </Box>
              
              {members.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No members in this department yet.
                </Typography>
              ) : (
                <DraggableList
                  items={members}
                  onReorder={(reordered) => handleReorder(reordered, departmentName)}
                  renderItem={(member, index, isDragging) => (
                    <Grid item xs={12}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              src={member.image}
                              alt={member.name}
                              sx={{ width: 60, height: 60, mr: 2 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" component="h3" gutterBottom>
                                {member.name}
                              </Typography>
                              <Chip 
                                label={member.position} 
                                color="primary" 
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          </Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ mb: 2 }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(member.bio) }}
                          />
                        </CardContent>
                        <CardActions>
                          <IconButton 
                            onClick={() => handleOpenDialog(member, departmentName)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDeleteMember(member, departmentName)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  )}
                />
              )}
            </Paper>
          ));
        })()
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Dr. Sarah Johnson"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                placeholder="e.g., Chief Executive Officer"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  value={selectedDepartment}
                  label="Department"
                  onChange={handleDepartmentChange}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Profile Image
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="team-member-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="team-member-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Upload Image
                  </Button>
                </label>
                {formData.imageFile && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Selected: {formData.imageFile.name} ({Math.round(formData.imageFile.size / 1024)}KB)
                  </Typography>
                )}
                {formData.imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={formData.imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #e0e0e0'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <RichTextEditor
                label="Biography"
                value={formData.bio}
                onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
                placeholder="Brief biography of the team member..."
                rows={4}
                helperText="Use the toolbar above to format your text with bold, italic, lists, and more"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<SaveIcon />}
          >
            {editingMember ? 'Update Member' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageTeamMembers; 